import { PLAN_RUN_ROW_KINDS, PLAN_RUN_STATUSES, PLAN_RUN_TRIGGERS } from '@steado/admin-contract/plan-runs';
import { z } from 'zod';

/**
 * Everything this screen filters on lives in the URL, so "every run the cron
 * failed to validate this week" is a link someone can paste into a thread.
 *
 * `.catch(undefined)` on every field: a hand-edited or stale URL should drop the
 * bad parameter and render, not throw a router error over a filter.
 */
export const planOpsSearchSchema = z.object({
  /**
   * Which plan the whole screen is about — the run list and the two panels
   * over it, so a kind that narrows the table narrows the numbers describing
   * it too.
   *
   * The API's third member, `all`, is the absence of this parameter rather
   * than a value of it: `?kind=all` is the default and does not belong in a
   * URL, and the row vocabulary is the one a run can actually be.
   */
  kind: z.enum(PLAN_RUN_ROW_KINDS).optional().catch(undefined),
  status: z.enum(PLAN_RUN_STATUSES).optional().catch(undefined),
  trigger: z.enum(PLAN_RUN_TRIGGERS).optional().catch(undefined),
  userId: z.string().uuid().optional().catch(undefined),
  /** An exact local date — the Monday a run was planning. */
  weekStartDate: z.string().trim().min(1).max(10).optional().catch(undefined),
  /**
   * Free text: the subscriber's name or phone, or a whole run id. Distinct
   * from `userId`, which is exact — an operator arriving from a support
   * thread has a name, not a uuid.
   */
  search: z.string().trim().min(1).max(120).optional().catch(undefined),
  cursor: z.string().optional().catch(undefined),
  /** The open run drawer, so a run is shareable and survives a refresh. */
  run: z.string().uuid().optional().catch(undefined),
});

export type PlanOpsSearch = z.infer<typeof planOpsSearchSchema>;
